import classNames from "classnames";

import { useCallback, useEffect, useState } from "react";

import { SvgIcon, Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import useEvent from "@/hooks/useEvent";

import style from "./style.module.scss";

interface Option {
  name: React.ReactNode;
  value: string;
}

interface SearchSelectProps {
  value: string;
  Icon?: typeof SvgIcon;
  options: Option[];
  onChange: (val: string) => void;
  border?: boolean;
  size?: "small" | "medium";
  block?: boolean;
}

const SearchSelect = (props: SearchSelectProps) => {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  const handleClose = useEvent(() => {
    setOpen(false);
  });

  const handleOpen = useEvent(() => {
    setOpen(true);
  });

  const handleMouseEnter = useEvent(() => {
    setHover(true);
  });

  const handleMouseLeave = useEvent(() => {
    setHover(false);
  });

  const renderValue = useCallback(
    (value: string) => {
      const select = props.options.find((ele) => ele.value === value);
      const Icon = props.Icon;

      return (
        <div className="flex item-center mt-[2.5px]">
          {Icon ? (
            <Icon
              fontSize="small"
              sx={{ color: (theme) => theme.palette.text.secondary, marginRight: "8px" }}
            />
          ) : null}

          <Typography
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            sx={{ lineHeight: "18px" }}
            color="textSecondary"
            variant="body2"
          >
            {select?.name}
          </Typography>
        </div>
      );
    },
    [props.options, props.Icon]
  );

  const renderSelectItem = useCallback(
    (data: Option[]) =>
      data.map((ele) => (
        <MenuItem key={ele.value} value={ele.value}>
          {ele.name}
        </MenuItem>
      )),
    []
  );

  useEffect(() => {
    if (!open) {
      setHover(false);
    }
  }, [open]);

  return (
    <Select
      size={props.size || "small"}
      open={open}
      onClose={handleClose}
      onOpen={handleOpen}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      value={props.value}
      renderValue={renderValue}
      MenuProps={{
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
      }}
      className={classNames([
        style.select,
        props.border ? "" : style.selectNoBorder,
        props.block ? style.block : null,
      ])}
      sx={{
        background: (theme) =>
          open ? theme.palette.action.selected : hover ? theme.palette.action.hover : "",
      }}
      onChange={(e: SelectChangeEvent) => props.onChange(e.target.value)}
    >
      {renderSelectItem(props.options)}
    </Select>
  );
};

export default SearchSelect;
